/**
 * Supafish header behaviour
 *
 * Handles:
 *  - Sticky header scroll state
 *  - Predictive search dropdown (Section Rendering API, with a JSON fallback)
 *  - Account dropdown menu
 *  - Live cart count / total badge
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Helpers
   * ------------------------------------------------------------------ */

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Formats an amount in cents using the shop's money format string.
   * Supports the placeholders Shopify exposes in `shop.money_format`.
   */
  function formatMoney(cents, format) {
    if (!format) return '';

    function withDelimiters(number, precision, thousands, decimal) {
      var value = (number / 100).toFixed(precision);
      var parts = value.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      if (precision === 0) return whole;
      return whole + decimal + parts[1];
    }

    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholder);
    if (!match) return format;

    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }

    return format.replace(placeholder, value);
  }

  /* ------------------------------------------------------------------ *
   * Sticky header shadow
   * ------------------------------------------------------------------ */

  function initScrollState(header) {
    function update() {
      header.classList.toggle('site-header--scrolled', window.scrollY > 8);
    }
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  /* ------------------------------------------------------------------ *
   * Predictive search
   * ------------------------------------------------------------------ */

  function PredictiveSearch(root) {
    this.root = root;
    this.form = root.querySelector('[data-predictive-search-form]');
    this.input = root.querySelector('[data-predictive-search-input]');
    this.panel = root.querySelector('[data-predictive-search-results]');
    this.clearBtn = root.querySelector('[data-predictive-search-clear]');
    this.spinner = root.querySelector('[data-predictive-search-spinner]');

    if (!this.form || !this.input || !this.panel) return;

    this.endpoint = this.form.getAttribute('data-predictive-search-url') || '/search/suggest';
    this.searchUrl = this.form.getAttribute('action') || '/search';
    this.strings = window.headerStrings || {};
    this.cache = {};
    this.controller = null;
    this.timer = null;
    this.activeIndex = -1;

    this.bind();
  }

  PredictiveSearch.prototype.bind = function () {
    var self = this;

    this.input.addEventListener('input', function () { self.onInput(); });
    this.input.addEventListener('focus', function () { self.onFocus(); });
    this.input.addEventListener('keydown', function (event) { self.onKeydown(event); });

    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', function () {
        self.input.value = '';
        self.clearBtn.hidden = true;
        self.close();
        self.input.focus();
      });
    }

    document.addEventListener('click', function (event) {
      if (!self.root.contains(event.target)) self.close();
    });
  };

  PredictiveSearch.prototype.items = function () {
    return Array.prototype.slice.call(this.panel.querySelectorAll('[data-predictive-item]'));
  };

  PredictiveSearch.prototype.onFocus = function () {
    if (this.input.value.trim().length < 2) return;
    if (!this.panel.innerHTML.trim()) return;
    this.open();
  };

  PredictiveSearch.prototype.onInput = function () {
    var self = this;
    var term = this.input.value.trim();

    if (this.clearBtn) this.clearBtn.hidden = term.length === 0;

    clearTimeout(this.timer);

    if (term.length < 2) {
      this.setLoading(false);
      this.close();
      return;
    }

    this.timer = setTimeout(function () { self.search(term); }, 250);
  };

  PredictiveSearch.prototype.search = function (term) {
    var self = this;

    if (this.cache[term] !== undefined) {
      this.render(this.cache[term]);
      return;
    }

    if (this.controller) this.controller.abort();
    this.controller = window.AbortController ? new AbortController() : null;
    this.setLoading(true);

    var controller = this.controller;

    this.fetchSection(term)
      .then(function (html) {
        // `null` means the section could not be rendered (e.g. the theme copy
        // on the store predates sections/predictive-search.liquid).
        if (html !== null) return html;
        return self.fetchJson(term);
      })
      .then(function (html) {
        if (html === null) return;
        self.cache[term] = html;
        self.render(html);
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        console.error('[predictive-search]', error);
        self.close();
      })
      .then(function () {
        // Only the most recent request may clear the loading state
        if (self.controller === controller) self.setLoading(false);
      });
  };

  PredictiveSearch.prototype.requestParams = function (term) {
    return (
      'q=' + encodeURIComponent(term) +
      '&resources[type]=product,collection,query' +
      '&resources[limit]=6' +
      '&resources[options][unavailable_products]=last'
    );
  };

  PredictiveSearch.prototype.fetchOptions = function () {
    return this.controller ? { signal: this.controller.signal } : {};
  };

  PredictiveSearch.prototype.fetchSection = function (term) {
    var url = this.endpoint + '?' + this.requestParams(term) + '&section_id=predictive-search';

    return fetch(url, this.fetchOptions())
      .then(function (response) {
        if (!response.ok) return null;
        return response.text();
      })
      .then(function (text) {
        if (text === null) return null;
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var section = doc.querySelector('#shopify-section-predictive-search');
        return section ? section.innerHTML : null;
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') throw error;
        return null;
      });
  };

  /** Fallback renderer used when the section isn't available on the store. */
  PredictiveSearch.prototype.fetchJson = function (term) {
    var self = this;
    var url = this.endpoint + '.json?' + this.requestParams(term);

    return fetch(url, this.fetchOptions())
      .then(function (response) {
        if (!response.ok) throw new Error('Predictive search failed: ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var resources = (data && data.resources && data.resources.results) || {};
        return self.renderFallback(term, resources);
      });
  };

  PredictiveSearch.prototype.renderFallback = function (term, resources) {
    var strings = this.strings;
    var products = resources.products || [];
    var queries = resources.queries || [];
    var collections = resources.collections || [];
    var html = '';

    if (!products.length) {
      if (!queries.length) {
        if (!collections.length) {
          return (
            '<div class="predictive-search__empty">' +
            '<p class="predictive-search__empty-title">' + escapeHtml(strings.noResults) + '</p>' +
            '</div>'
          );
        }
      }
    }

    function simpleGroup(heading, entries) {
      if (!entries.length) return '';
      var items = entries
        .map(function (entry) {
          return (
            '<li><a href="' + escapeHtml(entry.url) + '" class="predictive-search__item predictive-search__item--query" data-predictive-item>' +
            '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>' +
            '<span>' + escapeHtml(entry.title || entry.text) + '</span></a></li>'
          );
        })
        .join('');

      return (
        '<div class="predictive-search__group">' +
        '<p class="predictive-search__heading">' + escapeHtml(heading) + '</p>' +
        '<ul class="predictive-search__list">' + items + '</ul>' +
        '</div>'
      );
    }

    html += simpleGroup(strings.suggestions, queries);

    if (products.length) {
      html +=
        '<div class="predictive-search__group">' +
        '<p class="predictive-search__heading">' + escapeHtml(strings.products) + '</p>' +
        '<ul class="predictive-search__list">' +
        products
          .map(function (product) {
            var image = product.image || (product.featured_image && product.featured_image.url) || '';
            var thumb = image
              ? '<img src="' + escapeHtml(image) + '" alt="" loading="lazy" width="48" height="48">'
              : '';
            return (
              '<li><a href="' + escapeHtml(product.url) + '" class="predictive-search__item" data-predictive-item>' +
              '<span class="predictive-search__thumb">' + thumb + '</span>' +
              '<span class="predictive-search__info">' +
              '<span class="predictive-search__title">' + escapeHtml(product.title) + '</span>' +
              '</span></a></li>'
            );
          })
          .join('') +
        '</ul></div>';
    }

    html += simpleGroup(strings.collections, collections);

    html +=
      '<div class="predictive-search__footer">' +
      '<a href="' + escapeHtml(this.searchUrl) + '?q=' + encodeURIComponent(term) + '" class="predictive-search__view-all" data-predictive-item>' +
      escapeHtml(String(strings.viewAll || '').replace('__TERMS__', term)) +
      '</a></div>';

    return html;
  };

  PredictiveSearch.prototype.render = function (html) {
    this.panel.innerHTML = html;
    this.activeIndex = -1;

    if (this.panel.textContent.trim()) {
      this.open();
    } else {
      this.close();
    }
  };

  PredictiveSearch.prototype.setLoading = function (isLoading) {
    if (this.spinner) this.spinner.hidden = !isLoading;
  };

  PredictiveSearch.prototype.open = function () {
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  };

  PredictiveSearch.prototype.close = function () {
    this.panel.hidden = true;
    this.activeIndex = -1;
    this.input.setAttribute('aria-expanded', 'false');
  };

  PredictiveSearch.prototype.setActive = function (index) {
    var items = this.items();
    items.forEach(function (item, i) {
      item.classList.toggle('predictive-search__item--active', i === index);
      if (i === index) item.scrollIntoView({ block: 'nearest' });
    });
    this.activeIndex = index;
  };

  PredictiveSearch.prototype.onKeydown = function (event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (this.panel.hidden) return;

    var items = this.items();
    if (!items.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.setActive(this.activeIndex >= items.length - 1 ? 0 : this.activeIndex + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.setActive(this.activeIndex <= 0 ? items.length - 1 : this.activeIndex - 1);
    } else if (event.key === 'Enter') {
      if (this.activeIndex >= 0) {
        event.preventDefault();
        items[this.activeIndex].click();
      }
    }
  };

  /* ------------------------------------------------------------------ *
   * Account dropdown
   * ------------------------------------------------------------------ */

  function AccountMenu(root) {
    var toggle = root.querySelector('[data-account-toggle]');
    var panel = root.querySelector('[data-account-panel]');
    if (!toggle) return;
    if (!panel) return;

    function open() {
      panel.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
    }

    function close() {
      panel.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
    }

    toggle.addEventListener('click', function (event) {
      event.preventDefault();
      if (panel.hidden) {
        open();
      } else {
        close();
      }
    });

    document.addEventListener('click', function (event) {
      if (root.contains(event.target)) return;
      close();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') close();
    });
  }

  /* ------------------------------------------------------------------ *
   * Cart badge
   * ------------------------------------------------------------------ */

  function CartStatus(root) {
    this.root = root;
    this.countEl = root.querySelector('[data-cart-count]');
    this.totalEl = root.querySelector('[data-cart-total]');
    this.linkEl = root.querySelector('[data-cart-link]');
    this.moneyFormat = root.getAttribute('data-money-format') || '';
    this.strings = window.headerStrings || {};

    this.bind();
    this.sync();
  }

  CartStatus.prototype.bind = function () {
    var self = this;

    // Theme / app events that carry a cart payload
    document.addEventListener('cart:updated', function (event) {
      if (event.detail) self.update(event.detail.cart || event.detail);
    });

    document.addEventListener('shopify:cart:change', function (event) {
      if (event.detail) self.update(event.detail);
    });

    if (window.Shopify && typeof window.Shopify.on === 'function') {
      window.Shopify.on('cart:update', function (cart) { self.update(cart); });
    }

    // Refresh when the page is restored from the back/forward cache
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) self.sync();
    });
  };

  CartStatus.prototype.sync = function () {
    var self = this;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart fetch failed: ' + response.status);
        return response.json();
      })
      .then(function (cart) { self.update(cart); })
      .catch(function () { /* keep the server-rendered values */ });
  };

  CartStatus.prototype.update = function (cart) {
    if (!cart) return;

    var count = cart.item_count || 0;

    if (this.countEl) {
      this.countEl.textContent = count > 99 ? '99+' : count;
      this.countEl.hidden = count === 0;

      if (count > 0) {
        this.countEl.style.animation = 'none';
        void this.countEl.offsetHeight; // force reflow so the pop replays
        this.countEl.style.animation = '';
      }
    }

    if (this.totalEl) {
      if (typeof cart.total_price === 'number') {
        this.totalEl.textContent = formatMoney(cart.total_price, this.moneyFormat);
      }
    }

    if (this.linkEl) {
      var label = this.strings.cartCount || '';
      if (label) this.linkEl.setAttribute('aria-label', label.replace('__COUNT__', count));
    }
  };

  /* ------------------------------------------------------------------ *
   * Init
   * ------------------------------------------------------------------ */

  function init() {
    var header = document.querySelector('.site-header');
    if (!header) return;
    if (header.dataset.headerInitialized === 'true') return;
    header.dataset.headerInitialized = 'true';

    initScrollState(header);

    document.querySelectorAll('[data-predictive-search]').forEach(function (el) {
      new PredictiveSearch(el);
    });

    document.querySelectorAll('[data-account-menu]').forEach(function (el) {
      AccountMenu(el);
    });

    document.querySelectorAll('[data-cart-status]').forEach(function (el) {
      new CartStatus(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialise when the section is re-rendered in the theme editor
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.querySelector('.site-header')) init();
  });
})();
